import { useEffect, useState } from "react";
import { FolderPlus, Search, SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { CH_STATUS, FIC_TYPES, FILTER_LABEL, RATINGS, SORTS, WARNINGS, WORK_STATUS } from "@/lib/writing";

const uniqSorted = (arr) => [...new Map(arr.map((x) => [x.toLowerCase(), x])).values()].sort((a, b) => a.localeCompare(b));

// Every filter group with its [value, label] options, built from the writer's works
export const filterOptions = (works) => {
  const worlds = new Map(works.filter((w) => w.world_id).map((w) => [w.world_id, w.world_name || "World"]));
  const from = (k) => uniqSorted(works.flatMap((w) => w[k] || [])).map((x) => [x, x]);
  return {
    kind: [["original", "Original"], ["fanfiction", "Fanfiction"]], fic_type: FIC_TYPES, fandoms: from("fandoms"),
    rating: RATINGS.map(([k, l]) => [k, l]), warnings: [...WARNINGS, ...from("custom_warnings")],
    characters: from("characters"), relationships: from("relationships"),
    status: Object.entries(WORK_STATUS).map(([k, [l]]) => [k, l]), chapter_status: CH_STATUS.map(([k, l]) => [k, l]),
    tags: from("tags"), world: [...worlds.entries(), ["none", "No linked world"]],
  };
};
export const labelOf = (opts, key, v) => opts[key]?.find((o) => o[0].toLowerCase() === String(v).toLowerCase())?.[1] || v;

const Group = ({ k, opts, value, toggle }) => opts.length > 0 && (
  <div className="space-y-2" data-testid={`filter-group-${k}`}>
    <p className="font-code text-[10px] uppercase tracking-[0.25em] text-slate-500">{k === "chapter_status" ? "Has chapters that are" : FILTER_LABEL[k]}</p>
    <div className="flex flex-wrap gap-1.5">
      {opts.map(([v, l]) => {
        const on = value.includes(v);
        return <button key={v} onClick={() => toggle(k, v)} aria-pressed={on} data-testid={`filter-${k}-${String(v).toLowerCase().replace(/\s+/g, "-")}`}
          className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "border-amber-500 bg-amber-500/15 text-amber-200" : "border-line text-slate-400 hover:text-slate-200"}`}>{l}</button>;
      })}
    </div>
  </div>
);

const useMobile = () => {
  const [m, setM] = useState(window.innerWidth < 640);
  useEffect(() => { const f = () => setM(window.innerWidth < 640); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  return m;
};

export const WorkFilters = ({ works, q, setQ, filters, setFilters, sort, setSort, onSave, canSave }) => {
  const [open, setOpen] = useState(false);
  const mobile = useMobile();
  const opts = filterOptions(works);
  const toggle = (k, v) => { const cur = filters[k] || []; setFilters({ ...filters, [k]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] }); };
  const active = Object.entries(filters).flatMap(([k, vs]) => (vs || []).map((v) => [k, v]));
  const count = active.length;
  return (
    <div className="space-y-3 mb-8">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, tag, fandom, character…" className="pl-9 h-10" data-testid="works-search" />
        </div>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 h-10 rounded-full border border-line px-4 text-sm text-slate-300 hover:border-amber-500/40 hover:text-amber-300 transition-colors" data-testid="works-filters-btn">
          <SlidersHorizontal className="w-4 h-4" /> Filters{count > 0 && <span className="rounded-full bg-amber-500 text-onaccent text-[10px] font-bold px-1.5" data-testid="works-filters-count">{count}</span>}
        </button>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-10 rounded-full bg-inset border border-line text-xs text-slate-200 px-3" data-testid="works-sort">
          {SORTS.map(([k, l]) => <option key={k} value={k}>Sort: {l}</option>)}
        </select>
      </div>
      {count > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="active-filters">
          {active.map(([k, v]) => (
            <span key={`${k}-${v}`} className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/5 px-2.5 py-0.5 text-xs text-amber-100">
              <span className="text-slate-500">{FILTER_LABEL[k]}:</span> {labelOf(opts, k, v)}
              <button onClick={() => toggle(k, v)} className="text-slate-500 hover:text-rose-300" data-testid={`active-filter-remove-${k}-${String(v).toLowerCase().replace(/\s+/g, "-")}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
          <button onClick={() => setFilters({})} className="text-xs text-slate-400 hover:text-rose-300 px-2" data-testid="filters-clear-all">Clear all</button>
          {canSave && <button onClick={onSave} className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200 px-2" data-testid="save-as-collection-btn"><FolderPlus className="w-3.5 h-3.5" /> Save as collection</button>}
        </div>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side={mobile ? "bottom" : "right"} className={`bg-panel border-line overflow-y-auto ${mobile ? "max-h-[85vh]" : "w-full sm:max-w-md"}`} data-testid="works-filter-panel">
          <SheetTitle className="font-display text-3xl text-amber-100">Filters</SheetTitle>
          <SheetDescription className="text-slate-400 text-xs">Values in one group match any of them. Different groups must all match.</SheetDescription>
          <div className="space-y-5 mt-6">
            {Object.keys(opts).filter((k) => k !== "fic_type" || (filters.kind || []).includes("fanfiction") || (filters.fic_type || []).length)
              .map((k) => <Group key={k} k={k} opts={opts[k]} value={filters[k] || []} toggle={toggle} />)}
          </div>
          <div className="sticky bottom-0 mt-6 pt-4 pb-1 bg-panel flex gap-3">
            <button onClick={() => setFilters({})} className="flex-1 h-10 rounded-full border border-line text-sm text-slate-300" data-testid="filter-panel-clear">Clear all</button>
            <button onClick={() => setOpen(false)} className="flex-1 h-10 rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent text-sm font-semibold" data-testid="filter-panel-done">Show works</button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
